import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  ACTIONS_TEXT,
  IMAGE_SRC,
  ITEMS_PAGE_LABEL,
  STATUS_OPTIONS,
} from 'src/app/components/shared/shared-constants';

import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableHeaderComponent } from 'src/app/components/shared/table/components/table-header/table-header.component';
import {
  DENSITY,
  DENSITY_ABB,
  STOP_TYPE,
} from 'src/app/core/enums/operations.enum';
import {
  CreateDeliveryRequest,
  CreateDeliveryResponse,
} from 'src/app/core/models/operations/operations-delivery.model';
import {
  CreatePickupRequest,
  CreatePickupResponse,
} from 'src/app/core/models/operations/operations-pickup-stop.model';
import {
  OperationsRouteDetail,
  TableOperationsRouteDetails,
} from 'src/app/core/models/operations/operations-route-details.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { DeliveryService } from 'src/app/core/services/operations/delivery/delivery.service';
import { PickupService } from 'src/app/core/services/operations/pickup/pickup.service';
import { SummaryService } from 'src/app/core/services/operations/summary/summary.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { OperationsUtils } from 'src/app/core/utils/operations/operations.utils';
import { currentLocale } from 'src/app/locale.config';
import { AddDeliveryStopComponent } from '../add-operations-stop/add-delivery-stop/add-delivery-stop.component';
import { DeliveryStopReturn } from '../add-operations-stop/add-delivery-stop/add-delivery-stop.constants';
import { AddOperationsStopComponent } from '../add-operations-stop/add-operations-stop.component';
import {
  AddOperationsReturnData,
  POPUP_TYPE,
  STOP_TIME_TYPE,
} from '../add-operations-stop/add-operations-stop.constants';
import { EditOperationsStopComponent } from '../edit-operations-stop/edit-operations-stop.component';
import {
  ColumnIds,
  ColumnText,
  EXCEL_FILE_NAME,
  ROUTE_DETAILS_CONSTANTS,
  RouteDataType,
} from '../route-details.constants';

@Component({
  selector: 'app-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  standalone: false,
})
export class DetailsComponent implements OnInit {
  @ViewChild('header') header!: TableHeaderComponent;

  destroy = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private readonly locationService = inject(LocationService);

  tableRowElement!: HTMLCollection;
  dataSource = new MatTableDataSource<TableOperationsRouteDetails>();
  dataLoaded = false;

  readonly ITEMS_PAGE_LABEL = ITEMS_PAGE_LABEL;
  readonly CONSTANTS = ROUTE_DETAILS_CONSTANTS;
  readonly SRC = IMAGE_SRC;
  readonly HEADERS = ColumnText;
  readonly displayedColumns = Object.values(ColumnIds);
  readonly ACTIONS_TEXT = ACTIONS_TEXT;
  readonly SELECT_ALL = STATUS_OPTIONS.ALL_SELECTED;
  readonly IMAGES = IMAGE_SRC;

  selectedRoute = '';
  routerData!: RouteDataType;
  planDate: Date = new Date();
  toFromDate!: string;

  rowCount: number[] = [];
  locationCode = '';

  constructor(
    private readonly router: Router,
    private readonly commonUtils: CommonUtils,
    private readonly dialog: MatDialog,
    private readonly summaryService: SummaryService,
    private readonly datePipe: DatePipe,
    private readonly route: ActivatedRoute,
    private readonly deliveryService: DeliveryService,
    private readonly pickupService: PickupService,
    private readonly operationsUtils: OperationsUtils,

    private readonly dataService: DataService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.routerData = this.router.currentNavigation()?.extras
      ?.state as unknown as RouteDataType;
    this.dataService.editPendingStop$.subscribe(() => {
      this.updateRows();
      this.cdr.detectChanges();
    });
  }

  /**
   * Initializes route context (date and route id), subscribes to route param changes,
   * and kicks off the first data load via `updateRows()`.
   *
   * - `planDate` is set from the router-provided date.
   * - `toFromDate` is the formatted string used in backend calls.
   * - `selectedRoute` is populated from `ActivatedRoute.params`.
   * - Calls `updateRows()` to load user context and route details.
   */
  ngOnInit() {
    const transformedDate = this.datePipe.transform(
      this.routerData?.selectedDate,
      'dd-MM-yyyy',
    );

    this.planDate = new Date(this.routerData?.selectedDate);
    this.toFromDate = transformedDate ?? '';

    // Keep route id in sync with URL changes
    this.route?.params?.subscribe((param) => {
      this.selectedRoute = param.id;
    });

    this.updateRows();
  }

  /**
   * Exports the currently **filtered** table rows to Excel.
   * Builds a flat array of row objects mapped to exportable columns,
   * and delegates the file creation to `commonUtils.exportExcel`.
   *
   * @remarks
   * Uses `dataSource.filteredData`, ensuring the export reflects the active filter/search.
   * Column headers come from `ColumnText`, and file name from `EXCEL_FILE_NAME`.
   */
  exportExcel(): void {
    const filteredData = this.dataSource.filteredData.map((value, index) => {
      return {
        id: index + 1,
        stopType: value.type,
        density: value.density,
        cosignee: value.cosignee,
        address: value.address,
        city: value.city,
        postal: value.postal,
        completionTime: value.completionTime,
        trackingNumber: value.trackingNumber,
        commitTime: value.commitTime,
        eta: value.eta,
        scanCode: value.scanCode,
        description: value.description,
      };
    });

    this.commonUtils.exportExcel(
      Object.values(ColumnText),
      EXCEL_FILE_NAME,
      filteredData,
    );
  }

  /**
   * Applies a free-text search to the table using Angular Material's filter string,
   * and then recomputes `displayStop` so only the first row of each consecutive `index` group
   * is marked for display (grouping behavior).
   *
   * @param searchValue - Raw input from the header search; will be normalized to lowercase/trimmed.
   *
   * @remarks
   * - `MatTableDataSource` default filter is case-insensitive on lowercase JSON stringified rows.
   * - Grouping logic assumes rows are sorted by `index` and that same-index rows are adjacent.
   * - If a paginator is attached, consider resetting to the first page here.
   */
  search(searchValue: string): void {
    const trimmedValue = (searchValue ?? '').trim().toLowerCase();
    this.dataSource.filter = trimmedValue;

    // Recompute grouping flags after filter has been applied
    let currentIndex = -1;
    this.dataSource.filteredData.forEach((data) => {
      if (currentIndex !== data.index) {
        data.displayStop = true; // first of group
        currentIndex = data.index;
      } else {
        data.displayStop = false; // subsequent in same group
      }
    });
  }

  /**
   * Clears the table filter (shows all rows) and recomputes `displayStop`
   * so only the first item of each consecutive `index` group remains marked for display.
   *
   * @remarks
   * - Uses empty string `''` to clear the `MatTableDataSource` filter.
   * - Grouping logic is order-dependent (expects rows with same `index` to be adjacent).
   */
  clearSearch(): void {
    this.dataSource.filter = '';

    // Recompute grouping flags for the unfiltered data
    let currentIndex = -1;
    this.dataSource.filteredData.forEach((data) => {
      if (currentIndex === data.index) {
        data.displayStop = false; // subsequent in same group
      } else {
        data.displayStop = true; // first of group
        currentIndex = data.index;
      }
    });
  }

  /**
   * Loads the current user context and refreshes route details.
   * If the user's location changes while on this screen, navigates back to the route summary.
   *
   * @remarks
   * - Subscribes until component destroy using `takeUntilDestroyed(this.destroy)`.
   * - Calls `getRouteDetails()` whenever user context emits.
   * - Detects location change to prevent stale view and navigates to summary route.
   */
  updateRows(): void {
    this.userService
      .getCurrentUser$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((user) => {
        this.getRouteDetails();

        // Check for location change while viewing route details
        const previousLocationCode = this.locationCode;
        this.locationCode = user.location;

        if (
          previousLocationCode &&
          previousLocationCode !== this.locationCode
        ) {
          this.router.navigate([
            '/',
            currentLocale(),
            'operations',
            'route-summary',
          ]);
        }
      });
  }

  /**
   * Entry point to add a stop. Routes to either the delivery or pickup handler
   * based on the boolean flag from the header.
   *
   * @param isDeliveryStop - When true, creates a delivery stop; otherwise, a pickup stop.
   */
  addStop(isDeliveryStop: boolean): void {
    if (isDeliveryStop) {
      this.handleDeliveryStop();
    } else {
      this.handlePickupStop();
    }
  }

  /**
   * Fetches route details for the current route/date/location, builds the table rows,
   * sorts them by `index`, assigns to the data source, and (optionally) opens a completion popup.
   *
   * @param newId - If provided, triggers `openCompletePopup` with the raw scan data and this ID.
   *
   * @remarks
   * - Keeps the same `MatTableDataSource` instance; updates `.data` to preserve bindings/paginator/predicate.
   * - Sorting by `index` supports downstream grouping logic (`displayStop`).
   * - Calls `updateLoadingModal(false)` when data load completes.
   */
  getRouteDetails(newId: number | null = null): void {
    this.summaryService
      .getRouteDetails(
        this.locationCode,
        this.toFromDate,
        this.toFromDate,
        this.selectedRoute,
      )
      .pipe(take(1))
      .subscribe((res) => {
        const scan = res?.response?.scan ?? [];

        // Keep a simple count for existing logic
        this.rowCount.push(scan.length);

        // merge cached infoNotice per stopId
        // We do this BEFORE creating the table rows so the value flows into the dialog prefill.
        scan.forEach((s: any) => {
          try {
            const cached = this.dataService.getInfoNotice(s.id);
            if (cached) {
              s.infoNotice = cached;
            }
          } catch {
            // best-effort; do not fail routedetails load due to cache read
          }
        });

        // Map API scan → table rows (now includes injected infoNotice)
        const returnVals = this.operationsUtils.createDetailsTable(scan);

        // Ensure order by index to support grouping/displayStop logic
        returnVals.sort((a, b) => a.index - b.index);

        // Update existing dataSource instance
        this.dataSource.data = returnVals;

        this.commonUtils.updateLoadingModal(false);

        if (newId) {
          this.openCompletePopup(scan, newId);
        }
      });
  }

  /**
   * Maps a density enum/string to its corresponding abbreviation used in the UI.
   *
   * @param density - A density value (e.g., 'RESIDENTIAL', 'COMMERCIAL', 'UNKNOWN').
   * @returns The abbreviation: 'R' for residential, 'C' for commercial, 'U' for unknown/default.
   *
   * @remarks
   * Defaults to 'U' when density does not match known values.
   */
  getDensity(density: string): string {
    switch (density) {
      case DENSITY.RESIDENTIAL:
        return DENSITY_ABB.R;
      case DENSITY.COMMERCIAL:
        return DENSITY_ABB.C;
      case DENSITY.UNKNOWN:
        return DENSITY_ABB.U;
      default:
        return DENSITY_ABB.U;
    }
  }

  /**
   * Opens the Edit Operations Stop dialog for a given route detail.
   * Prior to opening, it clears both the datasource filter and the visible search input
   * to avoid any UI misalignment (e.g., colspan/rowspan based on filtered groups).
   *
   * Flow:
   * 1) Clears search state (datasource + header input).
   * 2) Builds `country$` observable for the location profile.
   * 3) Resolves full `routeDetails` object from current datasource rows.
   * 4) Opens `EditOperationsStopComponent` dialog with the necessary data.
   * 5) After dialog closes, if the operation succeeded (HTTP 200):
   *    - Refreshes route details.
   *    - Clears search again (keeps UI/grouping consistent after refresh).
   *    - Notifies via `dataService.notifyStopAdded()`.
   * 6) If required context is missing, shows a "cannot find route" banner.
   *
   * @param routeDetail - The row/route detail the user intends to edit.
   */
  editStop(routeDetail: OperationsRouteDetail) {
    // Clearing the search field to avoid misalignment issues (both datasource + header input)
    this.clearSearch();
    this.header?.clearSearchText();

    const country$ = this.locationService.getLocationProfile(this.locationCode);

    const routeDetails = this.operationsUtils.getSelectedRoute(
      routeDetail,
      this.dataSource.data,
    );

    if (!!routeDetails && !!this.selectedRoute && !!this.planDate) {
      this.dialog
        .open(EditOperationsStopComponent, {
          disableClose: true,
          minWidth: '72.25rem',
          minHeight: '30.75rem',
          data: {
            routeDetails: routeDetails,
            country: country$,
            route: this.selectedRoute,
            planDate: this.planDate,
          },
        })
        .afterClosed()
        .subscribe(
          (value: {
            request: CreateDeliveryRequest | CreatePickupRequest;
            response: CreateDeliveryResponse | CreatePickupResponse;
          }) => {
            this.commonUtils.updateLoadingModal(false);
            if (value?.response?.code === 200) {
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
              this.clearSearch();
            }
            this.dataService.notifyStopAdded();
            this.getRouteDetails();
          },
        );
    } else {
      this.commonUtils.updateBannerType(BANNER_TYPE.CANNOT_FIND_ROUTE);
    }
  }

  /**
   * Computes the rowspan for a given row, used to visually group consecutive
   * rows sharing the same `index`. Only rows with `displayStop === true`
   * (i.e., the first row of a group) receive a non-zero rowspan.
   *
   * @param data - A row from the operations route table.
   * @returns The number of consecutive rows with the same `index` (or 0 when not a group head).
   */
  getRowSpan(data: TableOperationsRouteDetails) {
    let returnValue = 0;
    if (data.displayStop) {
      returnValue = this.dataSource.filteredData.filter(
        (filteredData) => filteredData.index === data.index,
      ).length;
    }
    return returnValue;
  }

  /**
   * Opens the Add Delivery Stop flow:
   * - First dialog: `AddDeliveryStopComponent` (capture customer/tracking context).
   * - If tracking number search yields data, opens `AddOperationsStopComponent` pre-filled for delivery.
   * - If customer mode is selected, opens `AddOperationsStopComponent` for delivery without tracking.
   * - Handles "Back" navigation to re-open the add delivery flow when requested.
   *
   * After the operations dialog closes, forwards the returned payload to `handleReturnOperationStop`.
   */
  private handleDeliveryStop() {
    this.dialog
      .open(AddDeliveryStopComponent, {
        disableClose: true,
        data: { date: this.planDate, routeId: this.selectedRoute },
      })
      .afterClosed()
      .subscribe((value: DeliveryStopReturn) => {
        if (value?.trackNumSearch && value?.trackingNumData) {
          this.dialog
            .open(AddOperationsStopComponent, {
              disableClose: true,
              data: {
                route: this.selectedRoute,
                planDate: this.planDate,
                trackingNumData: value.trackingNumData,
                type: POPUP_TYPE.DELIVERY,
              },
            })
            .afterClosed()
            .subscribe((value: AddOperationsReturnData) => {
              if (value) {
                this.handleReturnOperationStop(value);
              }
            });
        }

        if (value?.customer) {
          this.dialog
            .open(AddOperationsStopComponent, {
              disableClose: true,
              data: {
                route: this.selectedRoute,
                planDate: this.planDate,
                trackingNumData: null,
                type: POPUP_TYPE.DELIVERY,
              },
            })
            .afterClosed()
            .subscribe((returnVal: AddOperationsReturnData) => {
              if (returnVal) {
                if (returnVal.back) {
                  this.addStop(true);
                } else {
                  if (value) {
                    this.handleReturnOperationStop(returnVal);
                  }
                }
              }
            });
        }
      });
  }

  /**
   * Opens the Add Pickup Stop flow directly via `AddOperationsStopComponent`.
   * After the dialog closes, any returned payload is forwarded to `handleReturnOperationStop`.
   */
  private handlePickupStop() {
    this.dialog
      .open(AddOperationsStopComponent, {
        disableClose: true,
        data: {
          route: this.selectedRoute,
          planDate: this.planDate,
          type: POPUP_TYPE.PICKUP,
        },
      })
      .afterClosed()
      .subscribe((value: AddOperationsReturnData) => {
        if (value) {
          this.handleReturnOperationStop(value);
        }
      });
  }

  /**
   * Handles the server-side creation of a pickup or delivery stop using the payload
   * returned by the "Add Operations" dialog. On success:
   * - Shows a success banner.
   * - Waits briefly (1s) to allow backend persistence to be visible.
   * - Refreshes route details; when the stop was completed (`STOP_TIME_TYPE.COMPLETE`),
   *   reopens the completion popup for the newly created stop.
   * - Notifies listeners that a stop was added.
   *
   * On known 400-type errors, delegates to `handleErrors()` for banner messaging.
   *
   * @param value - Payload returned from `AddOperationsStopComponent` containing request and meta.
   */
  private handleReturnOperationStop(value: AddOperationsReturnData) {
    this.commonUtils.updateLoadingModal(true);
    // Delivery
    if (value?.request?.stop?.stopType === STOP_TYPE.DELIVERY) {
      this.deliveryService
        .createDelivery(value.request as CreateDeliveryRequest)
        .pipe(take(1))
        .subscribe((response) => {
          this.commonUtils.updateLoadingModal(false);

          if (response.code === 200) {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);

            // Wait briefly to ensure DB updates propagate
            setTimeout(() => {
              if (value.planned === STOP_TIME_TYPE.COMPLETE) {
                this.getRouteDetails(response.response.stopId);
                this.dataService.notifyStopAdded();
              } else {
                this.getRouteDetails();
                this.dataService.notifyStopAdded();
              }
            }, 1000);
          } else if (response.error?.code === 400) {
            this.handleErrors(response.error);
          }
        });
    }

    // Pickup
    if (value?.request?.stop?.stopType === STOP_TYPE.PICKUP) {
      this.pickupService
        .createPickup(
          value.request as CreatePickupRequest,
          this.locationCode,
          this.selectedRoute,
        )
        .subscribe((response) => {
          this.commonUtils.updateLoadingModal(false);

          if (response.code === 200) {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);

            // Wait briefly to ensure DB updates propagate
            setTimeout(() => {
              if (value.planned === STOP_TIME_TYPE.COMPLETE) {
                this.getRouteDetails(response.response.stopId);
                this.dataService.notifyStopAdded();
              } else {
                this.getRouteDetails();
                this.dataService.notifyStopAdded();
              }
            }, 1000);
          } else if (response.error?.code === 400) {
            this.handleErrors(response.error);
          }
        });
    }
  }

  /**
   * Converts backend error codes/messages into user-facing banners.
   * Special-cases address-related validation keys to show precise messaging;
   * otherwise falls back to generic API 400 error banners.
   *
   * @param error - The error object returned from service calls.
   */
  handleErrors(error: {
    code?: number;
    deviceStatus?: string;
    errors?: [{ field?: string; message?: string }];
    message: any;
    status?: string;
  }) {
    switch (error.message) {
      case 'addr.custName.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_CUST_NAME);
        break;
      case 'addr.stNum.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_STREET_NUMBER);
        break;
      case 'addr.stPrefix.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_STREET_PREFIX);
        break;
      case 'addr.stSuffix.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_STREET_SUFFIX);
        break;
      case 'addr.stType.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_STREET_TYPE);
        break;
      case 'addr.suite.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_SUITE);
        break;
      case 'addr.floor.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_FLOOR);
        break;
      case 'addr.pd1.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_STATE);
        break;
      case 'addr.pd2.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_MUNIC);
        break;
      case 'addr.pd3.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_BARRIO);
        break;
      case 'addr.postal.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_POSTAL_CODE);
        break;
      case 'addr.stName.invalid':
        this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_STREET_NAME);
        break;
      default:
        if (error.message.includes('addr')) {
          this.commonUtils.updateBannerType(BANNER_TYPE.INCORRECT_ADDRESS);
          break;
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_400_ERROR);
        }
    }
  }

  /**
   * After a stop is created as "complete", opens the completion popup on that new stop.
   * If the stop cannot be found in the API data by `newId`, shows a "cannot find route" banner.
   *
   * @param apiData - Raw route detail list returned from the server.
   * @param newId - The ID of the newly created/target stop to open.
   */
  private openCompletePopup(apiData: OperationsRouteDetail[], newId: number) {
    const val = apiData.find((val) => val.id === newId);
    if (val) {
      this.editStop(val);
    } else {
      this.commonUtils.updateBannerType(BANNER_TYPE.CANNOT_FIND_ROUTE);
    }
  }
}
